import type { PayloadAction } from '@reduxjs/toolkit';
import {
  createGroupedNotification,
  findGroupableNotification,
  placeIdByCreatedAt,
  shouldGroupNotification,
} from 'src/helper/notification-utils';
import type { ViewListType } from 'src/redux/list-view/types';
import {
  type InitialProfileStateType,
  initialProfileState,
  type LdIdentifyFailureReason,
  type NotificationType,
  type ServiceRequestEntity,
  type SubMenuObj,
  type WorkOrderEntity,
} from 'src/redux/profile/types';
import { WORK_ORDER_OPERATIONAL_EXCEPTION_NOTIFICATION } from 'src/shared/constants/notifications';

/** How many live-cleared ids the store keeps (10-29): far more than To do ever lists at once. */
const LIVE_CLEARED_KEPT = 500;

const reducers = {
  setLdIdentified: (state: InitialProfileStateType) => {
    state.ldIdentified = true;
    // Clears a prior degrade: a slow `identify()` that resolves AFTER the timeout
    // fired is still a real identification, and the gated routes must recover to
    // the real content rather than stay parked on the error state.
    state.ldIdentifyFailed = false;
  },

  // APX-382 — degrades the LaunchDarkly gate so flag-gated routes can render an
  // error instead of `null` forever.
  //
  // The `reason` is what makes the guard below safe in both directions:
  //
  //   'timeout'  — a deadline elapsed. That is a GUESS about a call we cannot see
  //                the state of, so it must never override a success: a timer that
  //                fires just after a slow `identify()` landed would otherwise
  //                knock a healthy session onto the error card.
  //   'rejected' — the SDK itself rejected the call. That is authoritative, and it
  //                stays authoritative for a RE-identify: when the organization
  //                changes and the new `identify()` rejects, the flag context still
  //                holds the previous org's values, so continuing to render
  //                children against them is exactly the silent-wrong-answer this
  //                whole ticket is about. Clearing `ldIdentified` routes that case
  //                to the error card instead.
  setLdIdentifyFailed: (
    state: InitialProfileStateType,
    action: PayloadAction<{ reason: LdIdentifyFailureReason }>,
  ) => {
    if (state.ldIdentified && action.payload.reason !== 'rejected') return;
    state.ldIdentified = false;
    state.ldIdentifyFailed = true;
  },

  updateSideNav: (
    state: InitialProfileStateType,
    action: PayloadAction<{
      menuSlug: string;
      res: ViewListType[];
      defaultViewLabel: string;
    }>,
  ) => {
    const {
      payload: { res: actionItem, menuSlug, defaultViewLabel },
    } = action;
    const updatedData = actionItem?.filter((item) => item?.is_pinned);
    const newData: SubMenuObj[] = [];
    updatedData.forEach((item) => {
      const newItem = {
        name: item?.view?.label,
        view_uuid: item?.view?.value,
        is_primary: Boolean(item?.is_primary),
      };
      if (item?.is_primary && defaultViewLabel) {
        newItem.name = defaultViewLabel;
      }
      newData.push(newItem);
    });

    state.app.subMenus[menuSlug] = newData;
  },

  addSubMenuCount: (
    state: InitialProfileStateType,
    action: PayloadAction<{
      view_uuid: string;
      count: number;
    }>,
  ) => {
    state.app.subMenusCountDetails[action?.payload?.view_uuid] = action?.payload?.count;
  },

  removeNotification: (state: InitialProfileStateType) => {
    // To do items are shared with the other coordinators, so the bell's Clear all (like the
    // backend's) leaves them in place; only the other rows are emptied.
    const keptSource: typeof state.notificationSource = {};
    const keptIds: string[] = [];
    let keptUnread = 0;
    for (const id of state.notifications?.data || []) {
      const row = state.notificationSource?.[id];
      if (row?.data?.operational_exception_claim_id) {
        keptSource[id] = row;
        keptIds.push(id);
        if (!row.read_at) keptUnread += 1;
      }
    }
    state.notificationSource = keptSource;
    state.notifications = {
      data: keptIds,
      meta: {
        current_page: 0,
        from: 0,
        last: '',
        next: '',
        per_page: 0,
        prev: '',
        to: 0,
        total: keptIds.length,
        next_cursor: '',
        prev_cursor: '',
      },
      // Clear All empties the list but keeps its direction, so later live rows still land by it.
      ascending: state.notifications?.ascending,
    };
    state.app.userDetails.data.un_read_notification_count = keptUnread;
  },

  deleteEachNotification: (
    state: InitialProfileStateType,
    action: PayloadAction<{
      clearedAt?: string;
      notificationId: string;
    }>,
  ) => {
    if (action.payload?.notificationId) {
      const newSource = { ...(state.notificationSource || {}) };
      const newNotificationData = [...(state.notifications?.data || [])];
      let newClearedSource = { ...(state.clearedNotificationSource || {}) };

      const notification = newSource?.[action.payload?.notificationId];
      if (!notification) return;
      const clearedNotification = action.payload.clearedAt
        ? { ...notification, cleared_at: action.payload.clearedAt }
        : notification;

      // Handle grouped notifications - clear them as a group
      if (notification?.groupedNotificationIds && notification.groupedNotificationIds.length > 1) {
        // For grouped notifications, only count as 1 unread
        if (!notification?.read_at && state.app.userDetails.data.un_read_notification_count > 0) {
          state.app.userDetails.data.un_read_notification_count -= 1;
        }

        // Move the grouped notification to cleared
        newClearedSource = {
          ...newClearedSource,
          [action.payload?.notificationId]: clearedNotification,
        };
      } else {
        // Handle single notification normally
        if (!notification?.read_at && state.app.userDetails.data.un_read_notification_count > 0) {
          state.app.userDetails.data.un_read_notification_count -= 1;
        }
        newClearedSource = {
          ...newClearedSource,
          [action.payload?.notificationId]: clearedNotification,
        };
      }

      // Place only the cleared row; the cleared list keeps its server order.
      const newClearedIds = placeIdByCreatedAt(
        [...(state.clearedNotifications?.data || [])],
        action.payload.notificationId,
        newClearedSource,
        state.clearedNotifications?.ascending,
        state.workOrderEntities,
      );
      delete newSource?.[action.payload?.notificationId];
      if (newNotificationData?.includes(action.payload?.notificationId)) {
        state.notifications.data = newNotificationData?.filter(
          (id) => id !== action.payload?.notificationId,
        );
      }
      state.notificationSource = newSource;
      state.clearedNotificationSource = newClearedSource;
      state.clearedNotifications.data = newClearedIds;
    }
  },

  unClearEachNotification: (
    state: InitialProfileStateType,
    action: PayloadAction<{
      notificationId: string;
    }>,
  ) => {
    if (action?.payload?.notificationId) {
      const newSource = { ...(state.clearedNotificationSource || {}) };
      const clearedNotificationData = [...(state.clearedNotifications?.data || [])];
      let newNotificationSource = { ...(state.notificationSource || {}) };
      let newNotificationIds = [];
      if (!newSource?.[action.payload?.notificationId]?.read_at) {
        state.app.userDetails.data.un_read_notification_count += 1;
      }
      newNotificationSource = {
        ...newNotificationSource,
        [action.payload?.notificationId]: newSource?.[action.payload?.notificationId],
      };
      // Place only the un-cleared row; the list keeps its server order.
      newNotificationIds = placeIdByCreatedAt(
        [...(state.notifications?.data || [])],
        action.payload.notificationId,
        newNotificationSource,
        state.notifications?.ascending,
        state.workOrderEntities,
      );
      delete newSource?.[action.payload?.notificationId];
      if (clearedNotificationData?.includes(action.payload?.notificationId)) {
        state.clearedNotifications.data = clearedNotificationData?.filter(
          (id) => id !== action.payload?.notificationId,
        );
      }
      state.clearedNotificationSource = newSource;
      state.notificationSource = newNotificationSource;
      state.notifications.data = newNotificationIds;
    }
  },

  addNewNotification: (state: InitialProfileStateType, action: PayloadAction<NotificationType>) => {
    if (action.payload?.notification_id) {
      if (state.clearedNotificationSource?.[action.payload.notification_id]) {
        // A cleared row stays cleared. The exception is a self-described item alerting again
        // (a new text on a conversation item the coordinator had cleared): it leaves the cleared
        // list and is added back to the active list below, with what it says now. A row that
        // arrives cleared is not alerting, so it stays where it is.
        if (!action.payload.item || action.payload.cleared_at) return;

        const newClearedSource = { ...state.clearedNotificationSource };
        delete newClearedSource[action.payload.notification_id];
        state.clearedNotificationSource = newClearedSource;
        state.clearedNotifications.data = (state.clearedNotifications?.data || []).filter(
          (id) => id !== action.payload.notification_id,
        );
      }

      let newSource = { ...(state.notificationSource || {}) };
      let newNotificationData = [...(state.notifications?.data || [])];
      let shouldIncrementUnread = false;

      // Check if this notification should be grouped with an existing one
      if (shouldGroupNotification(action.payload)) {
        const existingNotificationId = findGroupableNotification(action.payload, newSource);

        if (existingNotificationId && newSource[existingNotificationId]) {
          // Group with existing notification
          const groupedNotification = createGroupedNotification(
            newSource[existingNotificationId],
            action.payload,
          );

          newSource = {
            ...newSource,
            [existingNotificationId]: groupedNotification,
          };

          // Don't add a new id; the group row's time changed, so move only that row
          // (the rest keep the server's order)
          newNotificationData = placeIdByCreatedAt(
            newNotificationData,
            existingNotificationId,
            newSource,
            state.notifications?.ascending,
            state.workOrderEntities,
          );

          // DON'T increment unread count when adding to existing group
          // The group already counts as 1 unread
        } else if (!newNotificationData?.includes(action.payload?.notification_id)) {
          // This is a new vendor message notification (not groupable with existing ones)
          // Initialize grouping fields for new vendor message notification
          const notificationWithGrouping = {
            ...action.payload,
            groupedCount: 1,
            groupedNotificationIds: [action.payload.notification_id],
            latestMessageTimestamp: action.payload.created_at,
          };

          newSource = {
            ...newSource,
            [action.payload?.notification_id]: notificationWithGrouping,
          };
          newNotificationData = placeIdByCreatedAt(
            newNotificationData,
            action.payload.notification_id,
            newSource,
            state.notifications?.ascending,
            state.workOrderEntities,
          );

          // This is a new group, so increment unread if not read/cleared
          shouldIncrementUnread = !action.payload?.read_at && !action.payload?.cleared_at;
          // This is a new group
        }
      } else if (
        (action.payload.item ||
          action.payload.type === WORK_ORDER_OPERATIONAL_EXCEPTION_NOTIFICATION) &&
        newNotificationData?.includes(action.payload.notification_id) &&
        newSource[action.payload.notification_id]
      ) {
        // A live re-alert on a listed row (a self-described item, or a work-order operational
        // exception whose text was refreshed): show what it says now and reopen it. The bell's
        // count comes from the server: the alert events refetch it, forced.
        newSource = {
          ...newSource,
          [action.payload.notification_id]: {
            ...newSource[action.payload.notification_id],
            item: action.payload.item ?? newSource[action.payload.notification_id].item,
            data: action.payload.data,
            read_at: action.payload.read_at,
            cleared_at: action.payload.cleared_at,
          },
        };
      } else if (!newNotificationData?.includes(action.payload?.notification_id)) {
        // Handle non-vendor message notifications normally
        newSource = {
          ...newSource,
          [action.payload?.notification_id]: action?.payload,
        };
        // Place only the new row; the rest keep the server's order
        newNotificationData = placeIdByCreatedAt(
          newNotificationData,
          action.payload.notification_id,
          newSource,
          state.notifications?.ascending,
          state.workOrderEntities,
        );

        // Normal notification, increment unread if not read/cleared
        shouldIncrementUnread = !action.payload?.read_at && !action.payload?.cleared_at;
        // Normal notification
      }

      if (shouldIncrementUnread) {
        state.app.userDetails.data.un_read_notification_count += 1;
      }
      state.notificationSource = newSource;
      state.notifications.data = newNotificationData;

      // Upsert full WO/SR entity from incoming notification
      if (action.payload.work_order?.work_order_id) {
        const workOrderId = action.payload.work_order.work_order_id;
        const known = state.workOrderEntities[workOrderId];
        state.workOrderEntities[workOrderId] = {
          ...(action.payload.work_order as WorkOrderEntity),
          // Demo (main-38 demo-merge M1): many broadcasts carry no priority; keep the known one
          // so the next live row on this job is still placed in the urgent block.
          priority: action.payload.work_order.priority?.value
            ? action.payload.work_order.priority
            : known?.priority,
        };
      }
      if (action.payload.service_request?.service_request_id) {
        state.serviceRequestEntities[action.payload.service_request.service_request_id] = action
          .payload.service_request as ServiceRequestEntity;
      }
    }
  },

  setActiveSidebar: (
    state: InitialProfileStateType,
    action: PayloadAction<InitialProfileStateType['activeSidebar']>,
  ) => {
    state.activeSidebar = action?.payload;
  },

  resetReadNotificationCount: (state: InitialProfileStateType) => {
    state.app.userDetails.data.un_read_notification_count = 0;
  },

  readNotificationCount: (
    state: InitialProfileStateType,
    action: PayloadAction<NotificationType>,
  ) => {
    if (action.payload?.notification_id) {
      const newSource = { ...(state.notificationSource || {}) };
      const notification = newSource[action.payload.notification_id];

      // If this is a grouped notification, mark all grouped notifications as read
      if (notification?.groupedNotificationIds && notification.groupedNotificationIds.length > 1) {
        // Mark the main notification as read
        newSource[action.payload.notification_id] = {
          ...notification,
          ...(action?.payload || {}),
        };

        // Decrease unread count based on grouped count
        if (!notification.read_at && action.payload.read_at) {
          // For grouped notifications, we only count as 1 unread
          state.app.userDetails.data.un_read_notification_count -= 1;
        }
      } else {
        // Handle single notification normally
        newSource[action.payload.notification_id] = {
          ...newSource[action.payload.notification_id],
          ...(action?.payload || {}),
        };
        if (newSource[action.payload.notification_id]?.read_at) {
          state.app.userDetails.data.un_read_notification_count -= 1;
        }
      }
      state.notificationSource = newSource;
    }
  },
  readClearedNotificationCount: (
    state: InitialProfileStateType,
    action: PayloadAction<NotificationType>,
  ) => {
    if (action.payload?.notification_id) {
      const newClearedSource = { ...(state.clearedNotificationSource || {}) };
      newClearedSource[action.payload.notification_id] = {
        ...newClearedSource[action.payload.notification_id],
        ...(action?.payload || {}),
      };
      state.clearedNotificationSource = newClearedSource;
    }
  },

  markReadAllNotification: (state: InitialProfileStateType) => {
    /** Update all read_at */
    const now = Date.now();
    Object.entries(state.notificationSource).forEach(([key, notification]) => {
      if (notification.read_at === null) {
        state.notificationSource[key].read_at = String(now);
      }
    });
  },

  deletePassedNotification: (
    state: InitialProfileStateType,
    action: PayloadAction<{
      workOrderId: string;
    }>,
  ) => {
    if (action.payload?.workOrderId) {
      const notifcation = Object.values(state.notificationSource).find(
        (notif: NotificationType) =>
          // `type` is stored bare — the `notification.` prefix seen in useNotificationPusher is the
          // Pusher event name, not the payload type (addNewNotification stores the body verbatim).
          notif.work_order?.work_order_id === action.payload?.workOrderId &&
          notif.type === 'vendor_request.dispatched',
      );
      if (!notifcation) return;
      const newSource = { ...(state.notificationSource || {}) };
      const newNotificationData = [...(state.notifications?.data || [])];

      if (
        !newSource?.[notifcation?.notification_id]?.read_at &&
        state.app.userDetails.data.un_read_notification_count > 0
      ) {
        state.app.userDetails.data.un_read_notification_count -= 1;
      }
      delete newSource?.[notifcation?.notification_id];
      if (newNotificationData?.includes(notifcation?.notification_id)) {
        state.notifications.data = newNotificationData?.filter(
          (id) => id !== notifcation?.notification_id,
        );
      }
      state.notificationSource = newSource;
    }
  },

  /**
   * 11-03: the server says this notification no longer exists (a 404 on marking it read, after Reset
   * demo or Clear Work Queue deleted it). Drop it from both lists, and from To do, as if cleared.
   */
  removeGoneNotification: (state: InitialProfileStateType, action: PayloadAction<string>) => {
    const id = action.payload;
    const listed = state.notificationSource?.[id];
    if (
      listed &&
      !listed.read_at &&
      (state.app.userDetails.data.un_read_notification_count ?? 0) > 0
    ) {
      state.app.userDetails.data.un_read_notification_count -= 1;
    }
    if (state.notificationSource) delete state.notificationSource[id];
    if (state.clearedNotificationSource) delete state.clearedNotificationSource[id];
    if (state.notifications?.data) {
      state.notifications.data = state.notifications.data.filter((each) => each !== id);
    }
    if (state.clearedNotifications?.data) {
      state.clearedNotifications.data = state.clearedNotifications.data.filter(
        (each) => each !== id,
      );
    }
    state.liveClearedNotificationIds = [
      ...(state.liveClearedNotificationIds ?? []).slice(-(LIVE_CLEARED_KEPT - 1)),
      id,
    ];
  },

  clearNotificationsListData: (state: InitialProfileStateType) => {
    state.notifications.data = initialProfileState?.notifications.data;
  },

  updateWorkOrderEntityStatus: (
    state: InitialProfileStateType,
    action: PayloadAction<{ work_order_id: string; status: WorkOrderEntity['status'] }>,
  ) => {
    const entity = state.workOrderEntities[action.payload.work_order_id];
    if (entity && action?.payload?.status) {
      entity.status = action?.payload?.status;
    }
  },

  updateServiceRequestEntityStatus: (
    state: InitialProfileStateType,
    action: PayloadAction<{ service_request_id: string; status: ServiceRequestEntity['status'] }>,
  ) => {
    const entity = state.serviceRequestEntities[action.payload.service_request_id];
    if (entity && action?.payload?.status) {
      entity.status = action?.payload?.status;
    }
  },

  changeWorkQueueTab: (state: InitialProfileStateType, action: PayloadAction<string>) => {
    if (state.currentActiveTab !== action.payload) {
      state.currentActiveTab = action.payload;
    }
  },

  countLiveNotificationEvent: (state: InitialProfileStateType) => {
    state.liveNotificationEvents = (state.liveNotificationEvents ?? 0) + 1;
  },

  /** 10-29: a To do item a live event said was cleared (anyone's action), so To do drops it on any page. */
  noteLiveCleared: (state: InitialProfileStateType, action: PayloadAction<string>) => {
    state.liveClearedNotificationIds = [
      ...(state.liveClearedNotificationIds ?? []).slice(-(LIVE_CLEARED_KEPT - 1)),
      action.payload,
    ];
  },

  countFeedbackEvent: (state: InitialProfileStateType) => {
    state.feedbackEvents = (state.feedbackEvents ?? 0) + 1;
  },

  /** QQ3: a live Landon pause change, so a box on that job reads its pause again. */
  countLandonPauseEvent: (
    state: InitialProfileStateType,
    action: PayloadAction<string | undefined>,
  ) => {
    state.landonPauseEvent = {
      workOrderId: action.payload ?? null,
      count: (state.landonPauseEvent?.count ?? 0) + 1,
    };
  },

  setTodoCount: (state: InitialProfileStateType, action: PayloadAction<string | undefined>) => {
    state.todoCount = action.payload;
  },

  refreshTodoCount: (state: InitialProfileStateType) => {
    state.todoCountReads = (state.todoCountReads ?? 0) + 1;
  },

  /**
   * To do's list and the badge read again: a job moved in or out of To do without an item event (a
   * Take over or Hand back). `showNew`: the read's new rows show at once, not behind "N new".
   */
  refreshTodo: (
    state: InitialProfileStateType,
    action: PayloadAction<{ showNew?: boolean } | undefined>,
  ) => {
    state.todoReads = (state.todoReads ?? 0) + 1;
    state.todoCountReads = (state.todoCountReads ?? 0) + 1;
    if (action.payload?.showNew) state.todoReadShown = state.todoReads;
  },

  refreshWithLandon: (state: InitialProfileStateType) => {
    state.withLandonReads = (state.withLandonReads ?? 0) + 1;
  },

  setTodoCountFromList: (state: InitialProfileStateType, action: PayloadAction<boolean>) => {
    state.todoCountFromList = action.payload;
  },
};

export default reducers;
